import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-hot-toast";
import userService from "../../api/services/userService";
import uploadService from "../../api/services/uploadService";
import {
  PHONE_DIAL_CODES,
  parseContactForDialSelect,
  buildFullContactNo,
  maxBirthDateIsoForAge18,
  minBirthDateIso120Years,
} from "../../data/phoneDialCodes";

function toDateInputValue(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

function apiErrorMessage(err, fallback) {
  if (!err || typeof err !== "object") return fallback;
  return err.message || fallback;
}

export default function EditUserProfileModal({ userId, isOpen, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [newPhotoFile, setNewPhotoFile] = useState(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState(null);
  const [phoneDial, setPhoneDial] = useState("+92");
  const [phoneNational, setPhoneNational] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    birthDate: "",
  });

  const birthMax = useMemo(() => maxBirthDateIsoForAge18(), []);
  const birthMin = useMemo(() => minBirthDateIso120Years(), []);

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setImageUrl("");
    setNewPhotoFile(null);
    setNewPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    try {
      const res = await userService.detailsUser(userId);
      const u = res?.details;
      if (!u) {
        toast.error("User not found or profile data is incomplete.");
        onClose();
        return;
      }
      setImageUrl(typeof u.imageUrl === "string" && u.imageUrl.trim() ? u.imageUrl.trim() : "");
      const { dial, national } = parseContactForDialSelect(u.contactNo);
      setPhoneDial(dial);
      setPhoneNational(national);
      const g = String(u.gender ?? "").trim().toLowerCase();
      setForm({
        firstName: u.firstName ?? "",
        lastName: u.lastName ?? "",
        gender: g === "female" ? "Female" : g === "male" ? "Male" : "",
        birthDate: toDateInputValue(u.birthDate),
      });
    } catch (e) {
      console.error(e);
      toast.error(apiErrorMessage(e, "Failed to load user"));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    loadUser();
  }, [isOpen, userId, loadUser]);

  useEffect(() => {
    if (isOpen) return;
    setNewPhotoFile(null);
    setNewPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoFile = (e) => {
    const f = e.target.files?.[0];
    setNewPhotoFile(f || null);
    setNewPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f && f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;
    const national = phoneNational.replace(/\D/g, "");
    const contactNo = buildFullContactNo(phoneDial, national);

    if (!form.firstName.trim() || !form.lastName.trim() || !national) {
      toast.error("First name, last name, and country code + phone are required.");
      return;
    }
    if (!form.gender) {
      toast.error("Please select a gender.");
      return;
    }
    if (national.length < 6) {
      toast.error("Enter a valid phone number (at least 6 digits after the country code).");
      return;
    }
    if (form.birthDate && form.birthDate > birthMax) {
      toast.error("Birth date must be at least 18 years ago.");
      return;
    }

    setSaving(true);
    try {
      let uploadedImageUrl;
      if (newPhotoFile) {
        const up = await uploadService.single(newPhotoFile);
        uploadedImageUrl = up?.imageUrl || null;
        if (!uploadedImageUrl) {
          toast.error(up?.message || "Image upload failed");
          setSaving(false);
          return;
        }
      }

      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        contactNo,
        gender: form.gender,
        birthDate: form.birthDate || undefined,
        ...(uploadedImageUrl ? { imageUrl: uploadedImageUrl } : {}),
      };

      Object.keys(body).forEach((k) => {
        if (body[k] === undefined) delete body[k];
      });

      const res = await userService.updateAppUserDetails(userId, body);
      if (res?.responseCode === 200 || res?.success) {
        toast.success(res?.message || "User updated successfully");
        await onSaved?.();
        onClose();
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (err) {
      const code = err?.responseCode;
      const msg = apiErrorMessage(err, "Update failed");
      if (code === 409 && /phone|number|contact/i.test(msg)) {
        toast.error("This phone number is already registered. Please use another number.");
      } else if (code === 400 && /birth|18/i.test(msg)) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-user-title">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 id="edit-user-title" className="text-lg font-bold text-gray-900">
            Edit user #{userId}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            aria-label="Close">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-500 border-t-transparent" />
            <p className="mt-4 text-gray-600 text-sm">Loading user…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <p className="text-xs text-gray-500">
              Only these fields can be edited from the admin panel. Optional fields left empty are not sent (existing
              values are kept).
            </p>

            <div className="flex flex-col items-center sm:items-start gap-2">
              <span className={labelClass}>Profile picture</span>
              {(newPhotoPreview || imageUrl) ? (
                <img
                  src={newPhotoPreview || imageUrl}
                  alt=""
                  className="h-24 w-24 rounded-full object-cover border-2 border-gray-200 shadow-sm bg-gray-50"
                />
              ) : (
                <div className="h-24 w-24 rounded-full border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[11px] text-gray-500 text-center px-2">
                  No photo
                </div>
              )}
              <div className="w-full">
                <label className={labelClass}>Replace photo (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoFile}
                  className="text-sm text-gray-700 w-full"
                />
                {newPhotoFile ? (
                  <p className="text-[11px] text-gray-500 mt-1">New photo will be saved when you click Save changes.</p>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>First name</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Last name</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Gender (required)</label>
                <select name="gender" value={form.gender} onChange={handleChange} className={inputClass} required>
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Contact (country code + number)</label>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                  <select
                    value={phoneDial}
                    onChange={(e) => setPhoneDial(e.target.value)}
                    className={`${inputClass} sm:max-w-[200px] shrink-0`}>
                    {PHONE_DIAL_CODES.map((pc) => (
                      <option key={pc.code} value={pc.code}>
                        {pc.code} ({pc.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="Phone number (digits only)"
                    value={phoneNational}
                    onChange={(e) => setPhoneNational(e.target.value.replace(/[^\d]/g, ""))}
                    className={`${inputClass} flex-1 min-w-0`}
                    required
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Birth date (must be 18+)</label>
                <input
                  name="birthDate"
                  type="date"
                  min={birthMin}
                  max={birthMax}
                  value={form.birthDate}
                  onChange={handleChange}
                  className={inputClass}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Only dates that make the user at least 18 are allowed.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 pb-2 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
